<div class="sentiment-settings-side-panel" *ngIf="panelOpen">
  <div class="side-panel-overlay" (click)="closePanel()"></div>
  <div class="side-panel-container" role="dialog" aria-modal="true" aria-labelledby="sa-settings-title">
    <div class="side-panel-header">
      <div class="side-panel-header__start">
        <div class="header-label" id="sa-settings-title">{{ config.copy.settingsPanelTitle }}</div>
        <span
          *ngIf="configLoaded && !loadingPanel && !loadError"
          class="sa-settings-current-version"
        >
          {{ currentVersionLabel }}
        </span>
      </div>
      <div class="header-actions">
        <button type="button" class="close-button" (click)="closePanel()" aria-label="Close">
          <img src="./assets/images/close.svg" alt="" />
        </button>
      </div>
    </div>

    <div class="side-panel-status" *ngIf="setupMode || loadError || loadingPanel">
      <p *ngIf="setupMode" class="sa-settings-setup-banner">
        Complete the required sections and save. Once taxonomy and industry are saved, the Sentiment Analysis
        dashboard will load for this project.
      </p>
      <p *ngIf="loadError" class="sa-settings-load-error" role="alert">{{ loadError }}</p>
      <p *ngIf="loadingPanel" class="sa-settings-loading">
        <span class="sa-settings-loading__spinner" aria-hidden="true"></span>
        Loading latest configuration…
      </p>
    </div>

    <ng-container *ngIf="!loadingPanel && configLoaded">
      <nav class="sa-settings-inner-tabs" aria-label="Settings sections">
        <button
          *ngFor="let tab of panelTabs"
          type="button"
          class="sa-settings-inner-tab"
          [class.sa-settings-inner-tab--active]="activeTab === tab.key"
          [attr.aria-selected]="activeTab === tab.key"
          (click)="selectTab($any(tab.key))"
        >
          {{ tab.label }}
        </button>
      </nav>

      <div class="side-panel-scroll-body">
        <section class="sa-getting-started sa-settings-forms">
          <ng-container *ngIf="activeTab === 'industry'">
            <h3 class="sa-getting-started__panel-title">Industry</h3>
            <p class="sa-getting-started__step-description">{{ industryCopy.description }}</p>
            <p class="sa-getting-started__config-note">{{ industryCopy.configNote }}</p>
            <div class="sa-getting-started__input-block">
              <label class="sa-getting-started__input-label" for="sa-settings-industry-name">{{
                industryCopy.nameLabel
              }}</label>
              <p class="sa-getting-started__field-hint">{{ industryCopy.nameHint }}</p>
              <input
                id="sa-settings-industry-name"
                type="text"
                class="sa-getting-started__text-input"
                [(ngModel)]="draft.industryName"
                [disabled]="!canWrite"
              />
            </div>
            <div class="sa-getting-started__input-block">
              <label class="sa-getting-started__input-label" for="sa-settings-industry-desc">{{
                industryCopy.descriptionLabel
              }}</label>
              <p class="sa-getting-started__field-hint">{{ industryCopy.descriptionHint }}</p>
              <textarea
                id="sa-settings-industry-desc"
                class="sa-getting-started__text-input sa-getting-started__textarea"
                rows="3"
                [(ngModel)]="draft.industryDescription"
                [disabled]="!canWrite"
              ></textarea>
            </div>
          </ng-container>

          <ng-container *ngIf="activeTab === 'taxonomy'">
            <h3 class="sa-getting-started__panel-title">Taxonomy</h3>
            <p class="sa-getting-started__step-description">{{ taxonomyCopy.description }}</p>
            <p class="sa-getting-started__config-note">{{ taxonomyCopy.configNote }}</p>
            <div class="sa-getting-started__info-card">
              <h4 class="sa-getting-started__info-card-title">CSV format</h4>
              <p class="sa-getting-started__info-card-body">
                Columns: <code>{{ taxonomyCopy.csvColumns }}</code>
              </p>
              <p class="sa-getting-started__info-card-hint">{{ taxonomyCopy.csvHint }}</p>
            </div>
            <ng-container *ngIf="config.useInsightsEngine; else taxonomyDisabled">
              <div class="sa-getting-started__input-block">
                <label class="sa-getting-started__input-label">Replace taxonomy (CSV)</label>
                <div class="sa-getting-started__file-row">
                  <label class="sa-getting-started__file-btn">
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      class="sa-getting-started__file-input"
                      [disabled]="!canWrite || saving"
                      (change)="onTaxonomyCsvSelected($event)"
                    />
                    {{ buttons.chooseCsv }}
                  </label>
                  <span *ngIf="taxonomyCsvFileName" class="sa-getting-started__filename">{{ taxonomyCsvFileName }}</span>
                </div>
                <p *ngIf="!canWrite" class="sa-getting-started__warn">{{ taxonomyCopy.noPermission }}</p>
                <p *ngIf="taxonomyPreviewLoading" class="sa-getting-started__status">Parsing CSV…</p>
                <p *ngIf="taxonomyPreviewError" class="sa-getting-started__error">{{ taxonomyPreviewError }}</p>
                <p
                  *ngIf="draft.taxonomy?.themes?.length && !taxonomyPreviewError"
                  class="sa-getting-started__preview-summary"
                >
                  <ng-container *ngIf="taxonomyCsvFileName; else savedTaxonomyHint">
                    New CSV ready ({{ draft.taxonomy?.themes?.length }} theme(s)) — applied when you save.
                  </ng-container>
                  <ng-template #savedTaxonomyHint>
                    Saved configuration ({{ draft.taxonomy?.themes?.length }} theme(s)).
                  </ng-template>
                </p>
                <app-sentiment-analysis-taxonomy-preview
                  *ngIf="draft.taxonomy?.themes?.length && !taxonomyPreviewError"
                  [taxonomy]="draft.taxonomy"
                  [title]="taxonomyCsvFileName ? 'New taxonomy preview' : 'Current taxonomy'"
                />
              </div>
            </ng-container>
            <ng-template #taxonomyDisabled>
              <p class="sa-getting-started__error">Insights engine is not enabled in this environment.</p>
            </ng-template>
          </ng-container>

          <ng-container *ngIf="activeTab === 'heatmap_cohorts'">
            <h3 class="sa-getting-started__panel-title">Heatmap cohorts</h3>
            <p class="sa-getting-started__step-description">{{ heatmapCohortsCopy.description }}</p>
            <p class="sa-getting-started__config-note">{{ heatmapCohortsCopy.configNote }}</p>
            <p *ngIf="cohortFieldsLoading" class="sa-getting-started__status">{{ heatmapCohortsCopy.loadingHint }}</p>
            <p *ngIf="!cohortFieldsLoading && !cohortFieldOptions.length" class="sa-getting-started__hint">
              {{ heatmapCohortsCopy.emptyHint }}
            </p>
            <p *ngIf="cohortFieldOptions.length" class="sa-getting-started__preview-summary">
              {{ heatmapCohortsCopy.selectedCountLabel }}: {{ cohortSelectedCount('heatmap') }}
            </p>
            <ul
              *ngIf="cohortFieldOptions.length"
              class="sa-getting-started__checkbox-list sa-settings-scroll-list"
            >
              <li
                *ngFor="let opt of cohortFieldOptions; trackBy: trackCohortField"
                class="sa-getting-started__checkbox-item"
              >
                <label
                  class="sa-getting-started__cohort-label"
                  (click)="onCohortCheckboxClick(opt.fieldId, opt.label, 'heatmap', $event)"
                >
                  <input
                    type="checkbox"
                    class="sa-getting-started__cohort-checkbox"
                    tabindex="-1"
                    [checked]="isCohortFieldSelected(opt.fieldId, 'heatmap')"
                    (click)="$event.preventDefault()"
                  />
                  <span>{{ opt.label }}</span>
                  <code class="sa-getting-started__field-code">{{ opt.fieldId }}</code>
                </label>
              </li>
            </ul>
          </ng-container>

          <ng-container *ngIf="activeTab === 'prompt'">
            <h3 class="sa-getting-started__panel-title">Prompt</h3>
            <p class="sa-getting-started__step-description">{{ promptCopy.description }}</p>
            <p class="sa-getting-started__config-note">{{ promptCopy.configNote }}</p>
            <div class="sa-getting-started__input-block">
              <label class="sa-getting-started__input-label" for="sa-settings-theme-mode">{{ promptCopy.themeModeLabel }}</label>
              <select
                id="sa-settings-theme-mode"
                class="sa-getting-started__text-input"
                [(ngModel)]="draft.taxonomyThemeMode"
                [disabled]="!canWrite"
              >
                <option *ngFor="let m of taxonomyThemeModeOptions" [value]="m.value">{{ m.label }}</option>
              </select>
            </div>
            <div class="sa-getting-started__input-block">
              <label class="sa-getting-started__input-label" for="sa-settings-subtheme-mode">{{
                promptCopy.subthemeModeLabel
              }}</label>
              <select
                id="sa-settings-subtheme-mode"
                class="sa-getting-started__text-input"
                [(ngModel)]="draft.taxonomySubthemeMode"
                [disabled]="!canWrite"
              >
                <option *ngFor="let m of taxonomyThemeModeOptions" [value]="m.value">{{ m.label }}</option>
              </select>
            </div>
            <div class="sa-getting-started__input-block">
              <label class="sa-getting-started__input-label" for="sa-settings-theme-instruction">{{
                promptCopy.themeInstructionLabel
              }}</label>
              <p class="sa-getting-started__field-hint">{{ promptCopy.themeInstructionHint }}</p>
              <textarea
                id="sa-settings-theme-instruction"
                class="sa-getting-started__text-input sa-getting-started__textarea"
                rows="5"
                [(ngModel)]="draft.themeDomainInstruction"
                [disabled]="!canWrite"
              ></textarea>
            </div>
          </ng-container>

          <ng-container *ngIf="activeTab === 'summary'">
            <h3 class="sa-getting-started__panel-title">Summary</h3>
            <p class="sa-getting-started__step-description">{{ summaryCopy.description }}</p>
            <p class="sa-getting-started__config-note">{{ summaryCopy.configNote }}</p>

            <h4 class="sa-getting-started__section-label">{{ summaryCohortsCopy.selectedCountLabel }}</h4>
            <p class="sa-getting-started__hint">{{ summaryCohortsCopy.description }}</p>
            <p class="sa-getting-started__field-hint">{{ summaryCohortsCopy.copyFromHeatmapHint }}</p>
            <div *ngIf="cohortFieldOptions.length" class="sa-getting-started__actions">
              <button type="button" class="sa-getting-started__btn" (click)="copySummaryCohortsFromHeatmap()">
                Copy heatmap selection
              </button>
            </div>
            <p *ngIf="cohortFieldsLoading" class="sa-getting-started__status">{{ summaryCohortsCopy.loadingHint }}</p>
            <p *ngIf="!cohortFieldsLoading && !cohortFieldOptions.length" class="sa-getting-started__hint">
              {{ summaryCohortsCopy.emptyHint }}
            </p>
            <p *ngIf="cohortFieldOptions.length" class="sa-getting-started__preview-summary">
              {{ summaryCohortsCopy.selectedCountLabel }}: {{ cohortSelectedCount('summary') }}
            </p>
            <ul
              *ngIf="cohortFieldOptions.length"
              class="sa-getting-started__checkbox-list sa-settings-scroll-list"
            >
              <li
                *ngFor="let opt of cohortFieldOptions; trackBy: trackCohortField"
                class="sa-getting-started__checkbox-item"
              >
                <label
                  class="sa-getting-started__cohort-label"
                  (click)="onCohortCheckboxClick(opt.fieldId, opt.label, 'summary', $event)"
                >
                  <input
                    type="checkbox"
                    class="sa-getting-started__cohort-checkbox"
                    tabindex="-1"
                    [checked]="isCohortFieldSelected(opt.fieldId, 'summary')"
                    (click)="$event.preventDefault()"
                  />
                  <span>{{ opt.label }}</span>
                  <code class="sa-getting-started__field-code">{{ opt.fieldId }}</code>
                </label>
              </li>
            </ul>

            <h4 class="sa-getting-started__section-label sa-getting-started__section-label--spaced">Precompute</h4>
            <label
              class="sa-getting-started__checkbox-item sa-getting-started__checkbox-item--inline sa-getting-started__checkbox-item--stacked"
            >
              <span class="sa-getting-started__widget-label">
                <input type="checkbox" [(ngModel)]="draft.summaryPrecomputeEnabled" [disabled]="!canWrite" />
                <span class="sa-getting-started__widget-label-text">{{ summaryCopy.precomputeLabel }}</span>
              </span>
              <p class="sa-getting-started__widget-desc">{{ summaryCopy.precomputeHint }}</p>
            </label>
            <ng-container *ngIf="draft.summaryPrecomputeEnabled">
              <h4 class="sa-getting-started__section-label">{{ summaryCopy.sentimentsTitle }}</h4>
              <p class="sa-getting-started__hint">{{ summaryCopy.sentimentsHint }}</p>
              <p
                *ngIf="!draft.summaryPrecomputeSentiments.length"
                class="sa-getting-started__warn"
              >
                {{ summaryCopy.sentimentsEmpty }}
              </p>
              <ul class="sa-getting-started__checkbox-list">
                <li
                  *ngFor="let id of summarySentimentChoices"
                  class="sa-getting-started__checkbox-item"
                >
                  <label
                    class="sa-getting-started__cohort-label"
                    (click)="onSummarySentimentCheckboxClick(id, $event)"
                  >
                    <input
                      type="checkbox"
                      class="sa-getting-started__cohort-checkbox"
                      tabindex="-1"
                      [checked]="isSummarySentimentSelected(id)"
                      (click)="$event.preventDefault()"
                    />
                    <span>{{ summarySentimentChoiceLabel(id) }}</span>
                    <code class="sa-getting-started__field-code">{{ id }}</code>
                  </label>
                </li>
              </ul>

              <h4 class="sa-getting-started__section-label sa-getting-started__section-label--spaced">{{ summaryCopy.themeAllowlistTitle }}</h4>
              <p class="sa-getting-started__hint">{{ summaryCopy.themeAllowlistHint }}</p>
              <ul
                *ngIf="draft.summaryThemeAllowlist.length"
                class="sa-getting-started__theme-allowlist sa-settings-scroll-list sa-settings-scroll-list--compact"
              >
                <li *ngFor="let name of draft.summaryThemeAllowlist" class="sa-getting-started__theme-allowlist-item">
                  <span class="sa-getting-started__theme-allowlist-name">{{ name }}</span>
                  <button
                    type="button"
                    class="sa-getting-started__btn sa-getting-started__btn--small"
                    [disabled]="!canWrite"
                    (click)="removeThemeFromAllowlist(name)"
                  >
                    {{ summaryCopy.removeThemeButton }}
                  </button>
                </li>
              </ul>
              <div
                *ngIf="taxonomyThemeNames.length"
                class="sa-getting-started__input-block sa-getting-started__theme-add-row"
              >
                <label class="sa-getting-started__input-label" for="sa-settings-theme-add">{{
                  summaryCopy.addThemeLabel
                }}</label>
                <div class="sa-getting-started__file-row">
                  <select
                    id="sa-settings-theme-add"
                    class="sa-getting-started__text-input sa-getting-started__theme-select"
                    [(ngModel)]="summaryThemeToAdd"
                    [disabled]="!canWrite"
                  >
                    <option value="">{{ summaryCopy.addThemePlaceholder }}</option>
                    <option *ngFor="let name of themesAvailableToAdd" [value]="name">{{ name }}</option>
                  </select>
                  <button
                    type="button"
                    class="sa-getting-started__btn sa-getting-started__btn--primary"
                    [disabled]="!canWrite || !summaryThemeToAdd"
                    (click)="addSelectedThemeToAllowlist()"
                  >
                    {{ summaryCopy.addThemeButton }}
                  </button>
                  <button
                    type="button"
                    class="sa-getting-started__btn"
                    [disabled]="!canWrite || !themesAvailableToAdd.length"
                    (click)="addAllTaxonomyThemesToAllowlist()"
                  >
                    {{ summaryCopy.addAllThemesButton }}
                  </button>
                </div>
              </div>
            </ng-container>
          </ng-container>
        </section>
      </div>
    </ng-container>

    <div class="side-panel-footer">
      <button type="button" class="btn-cancel" (click)="closePanel()">Cancel</button>
      <button
        *ngIf="canWrite"
        type="button"
        class="btn-save"
        [disabled]="saving || loadingPanel || !canSave"
        (click)="saveAll()"
      >
        {{ saving ? config.copy.settingsPanelSaving : config.copy.settingsPanelSave }}
      </button>
    </div>
  </div>
</div>
